import type { AutomationType } from '@playbook-technologies/common-enums';

import type { Async } from '@client/new/helpers/types';

import { AUTOMATION_COPY, AUTOMATION_ORDER } from '../automation-copy';
import {
  AutomationsLoadError,
  AutomationsModel,
  AutomationsUpdateError,
  AutomationsUpdateStatus,
  useAutomationsModel,
} from '../model/Automations.model';

type AutomationRow = {
  automationType: AutomationType;
  label: string;
  description: string;
  enabled: boolean;
  pending: boolean;
  error?: string;
  retry?: () => Promise<void>;
  status?: string;
};

type AutomationsViewModel = {
  rows: AutomationRow[];
  retry: AutomationsModel['retry'];
  update: AutomationsModel['update'];
};

type AutomationsViewModelError = {
  message: string;
  retry: () => Promise<void>;
};

const LOAD_ERROR_MESSAGES: Record<AutomationsLoadError['kind'], string> = {
  unauthorized:
    'Your session has expired. Sign in again to manage automations.',
  forbidden: "You don't have permission to manage automations.",
  network: "We couldn't load automations.",
  contract: 'No automation types are available.',
};

const UPDATE_ERROR_MESSAGES: Record<AutomationsUpdateError['kind'], string> = {
  unauthorized:
    'Your session has expired. Your previous setting has been restored.',
  forbidden:
    "You don't have permission to change this automation. Your previous setting has been restored.",
  network:
    "We couldn't update this automation. Your previous setting has been restored. Try again.",
};

const statusMessage = (
  status: AutomationsUpdateStatus | undefined,
  label: string,
): string | undefined => {
  if (!status) return undefined;
  return status.kind === 'saving' ? `Saving ${label}` : `${label} saved`;
};

export const useAutomationsViewModel = (
  creatorId: number,
): Async<AutomationsViewModel, AutomationsViewModelError> => {
  const model = useAutomationsModel(creatorId);
  if (model.type === 'fetch-error') {
    return {
      type: 'fetch-error',
      value: {
        message: LOAD_ERROR_MESSAGES[model.value.kind],
        retry: model.value.retry,
      },
    };
  }
  if (model.type !== 'fetched') return model;

  return {
    type: 'fetched',
    value: {
      rows: AUTOMATION_ORDER.flatMap((automationType) => {
        const automation = model.value.automations.find(
          (item) => item.automationType === automationType,
        );
        const failure = model.value.errors[automationType];
        const { label, description } = AUTOMATION_COPY[automationType];
        return automation
          ? [
              {
                automationType,
                label,
                description,
                enabled: automation.enabled,
                pending: model.value.pending.has(automationType),
                error: failure
                  ? UPDATE_ERROR_MESSAGES[failure.kind]
                  : undefined,
                retry: failure
                  ? () =>
                      model.value.update(
                        automationType,
                        failure.intendedEnabled,
                      )
                  : undefined,
                status: statusMessage(
                  model.value.statuses[automationType],
                  label,
                ),
              },
            ]
          : [];
      }),
      retry: model.value.retry,
      update: model.value.update,
    },
  };
};
